import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp 
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import '../../css/accounts/ManageUsers.css';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form States
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('sub_admin');
  const [status, setStatus] = useState('Active');
  const [editingId, setEditingId] = useState(null);

  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 1. Fetch Users from Firestore 'adminUsers' Collection
  useEffect(() => {
    const q = query(collection(db, 'adminUsers'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setUsers(records);
      setFilteredUsers(records);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching admin users: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 2. Filter Logic
  useEffect(() => {
    let result = users;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          (user.name && user.name.toLowerCase().includes(term)) ||
          (user.email && user.email.toLowerCase().includes(term)) ||
          (user.mobile && user.mobile.includes(term))
      );
    }

    setFilteredUsers(result);
    setCurrentPage(1);
  }, [searchTerm, users]);

  // Reset Form
  const resetForm = () => {
    setName('');
    setMobile('');
    setEmail('');
    setPassword('');
    setRole('sub_admin');
    setStatus('Active');
    setEditingId(null);
  };

  // 🎯 CREATE OR UPDATE USER
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        // EDIT MODE: Update Firestore Details (Password update requires Cloud Functions, so we only update details here)
        await updateDoc(doc(db, 'adminUsers', editingId), {
          name: name.trim(),
          mobile: mobile.trim(),
          role: role,
          status: status,
          updatedAt: serverTimestamp(),
        });
        alert("User details updated successfully!");
        resetForm();
      } else {
        // CREATE MODE: Create Firebase Auth User & Save to Firestore
        if (password.length < 6) return alert("Password must be at least 6 characters long.");
        
        const confirmMsg = "Note: Creating a new user will temporarily log you out of your current session by Firebase default behavior. You will need to log back in as Super Admin. Proceed?";
        if (!window.confirm(confirmMsg)) return;

        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const newUid = userCredential.user.uid;

        // Save User Data in 'adminUsers' collection using the new UID
        await setDoc(doc(db, 'adminUsers', newUid), {
          name: name.trim(),
          mobile: mobile.trim(),
          email: email.trim(),
          role: role,
          status: status,
          createdAt: serverTimestamp(),
        });

        alert(`New user ${name} created successfully! Please log in again.`);
        resetForm();
        
        // Auto sign-out to clear the newly created user's session from current browser
        auth.signOut();
        window.location.href = '/admin-login'; 
      }
    } catch (error) {
      alert("Error saving user: " + error.message);
    }
  };

  // 🎯 EDIT USER CLICK
  const handleEditClick = (user) => {
    setEditingId(user.id);
    setName(user.name || '');
    setMobile(user.mobile || '');
    setEmail(user.email || '');
    setRole(user.role || 'sub_admin');
    setStatus(user.status || 'Active');
  };

  // 🎯 DELETE USER
  const handleDeleteClick = async (user) => {
    if (window.confirm(`Are you sure you want to delete the user "${user.name}"? This removes their database access record.`)) {
      try {
        await deleteDoc(doc(db, 'adminUsers', user.id));
        alert("User record deleted successfully!");
      } catch (error) {
        alert("Error deleting user: " + error.message);
      }
    }
  };

  // KPI Calculations
  const totalUsers = filteredUsers.length;
  const activeSubAdmins = filteredUsers.filter(u => u.role === 'sub_admin' && u.status === 'Active').length;
  const activeSuperAdmins = filteredUsers.filter(u => u.role === 'super_admin' && u.status === 'Active').length;

  // Pagination Calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayedUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="mu-container">
      <div className="mu-card">
        
        {/* Header */}
        <div className="mu-header">
          <div>
            <h2 className="mu-title">Manage Admin Users</h2>
            <p className="mu-subtitle">Create and manage access for Super Admins and Sub Admins</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="mu-kpi-grid">
          <div className="mu-kpi-card kpi-total">
            <span className="mu-kpi-label">Total Users</span>
            <span className="mu-kpi-value">{totalUsers}</span>
          </div>
          <div className="mu-kpi-card kpi-sub">
            <span className="mu-kpi-label">Active Sub Admins</span>
            <span className="mu-kpi-value">{activeSubAdmins}</span>
          </div>
          <div className="mu-kpi-card kpi-super">
            <span className="mu-kpi-label">Active Super Admins</span>
            <span className="mu-kpi-value">{activeSuperAdmins}</span>
          </div>
        </div>

        {/* Add / Edit Form */}
        <div className="mu-form-wrapper">
          <h3>{editingId ? "✏️ Edit User Access" : "➕ Create New Admin User"}</h3>
          <form onSubmit={handleSubmit} className="mu-form">
            <div className="form-grid">
              
              <div className="form-group">
                <label>Full Name <span className="req">*</span></label>
                <input 
                  type="text" 
                  placeholder="e.g., John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Mobile Number <span className="req">*</span></label>
                <input 
                  type="text" 
                  placeholder="e.g., 0501234567"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Address <span className="req">*</span></label>
                <input 
                  type="email" 
                  placeholder="admin@mrwhite.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={editingId !== null} // Email change blocked in edit mode
                />
              </div>

              {!editingId && (
                <div className="form-group">
                  <label>Login Password <span className="req">*</span></label>
                  <input 
                    type="password" 
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Assign Role <span className="req">*</span></label>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="sub_admin">Sub Admin (No Accounts Access)</option>
                  <option value="super_admin">Super Admin (Full Access)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Account Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="Active">Active (Can Login)</option>
                  <option value="Inactive">Inactive (Blocked)</option>
                </select>
              </div>

            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save">
                {editingId ? "Update User Details" : "Create User Account"}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="btn-cancel">
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Filter Bar */}
        <div className="mu-filter-bar">
          <input 
            type="text" 
            placeholder="Search by Name, Email, or Mobile..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mu-input"
          />
        </div>

        {/* Users Table */}
        <div className="mu-table-wrapper">
          <table className="mu-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>S.No</th>
                <th>Full Name</th>
                <th>Email ID</th>
                <th>Mobile Number</th>
                <th style={{ textAlign: 'center' }}>Role</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center', width: '130px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="status-cell">Loading users...</td>
                </tr>
              ) : currentDisplayedUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="status-cell">No users found.</td>
                </tr>
              ) : (
                currentDisplayedUsers.map((user, index) => {
                  const serialNumber = indexOfFirstItem + index + 1;

                  return (
                    <tr key={user.id} className={editingId === user.id ? 'editing-row' : ''}>
                      <td><strong>{serialNumber}</strong></td>
                      <td className="mu-name-text">{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.mobile}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`role-pill ${user.role}`}>
                          {user.role === 'super_admin' ? 'Super Admin' : 'Sub Admin'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`status-pill ${user.status?.toLowerCase() || 'active'}`}>
                          {user.status || 'Active'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="action-buttons">
                          <button 
                            onClick={() => handleEditClick(user)} 
                            className="btn-edit-sm"
                          >
                            ✏️ Edit
                          </button>
                          
                          <button 
                            onClick={() => handleDeleteClick(user)} 
                            className="btn-delete-sm"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {!loading && filteredUsers.length > 0 && (
          <div className="mu-pagination-bar">
            <div className="pagination-info">
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredUsers.length)}</strong> of <strong>{filteredUsers.length}</strong> users
            </div>

            <div className="pagination-buttons">
              <button 
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="page-btn nav-btn"
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prev && page - prev > 1 && <span className="pagination-dots">...</span>}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`page-btn ${currentPage === page ? 'active-page' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button 
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="page-btn nav-btn"
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ManageUsers;